import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import API, { getApiUrl } from '../../utils/api';
import '../../styles/Login.css';

function LoginScreen() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [loginMethod, setLoginMethod] = useState('password'); // 'password' o 'fingerprint'
  const [isCapturing, setIsCapturing] = useState(false);
  const [fingerprintStatus, setFingerprintStatus] = useState('');
  const { setCurrentUser, currentUser } = useAuth();
  const navigate = useNavigate();

  // Redirigir si ya está autenticado
  useEffect(() => {
    if (currentUser) {
      navigate('/dashboard');
    }
  }, [currentUser, navigate]);

  // Registrar el módulo al cargar el componente
  useEffect(() => {
    const registerModule = async () => {
      try {
        // Obtener información del sistema para IP y nombre de equipo
        // En Electron podemos usar APIs nativas
        const ip = window.electron ? await window.electron.getIPAddress() : '127.0.0.1';
        const hostname = window.electron ? await window.electron.getHostname() : 'local-device';

        await API.modulos.registrarModulo(ip, hostname);
      } catch (error) {
        console.error('Error al registrar módulo:', error);
        // No interrumpir el proceso de login si falla el registro del módulo
      }
    };

    registerModule();
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();

    if (loginMethod === 'password') {
      await handlePasswordLogin();
    } else {
      await handleFingerprintLogin();
    }
  }

  async function handlePasswordLogin() {
    if (!username || !password) {
      setError('Por favor ingrese usuario y contraseña');
      return;
    }

    try {
      setError('');
      setLoading(true);

      // Llamar a la API para autenticar
      const response = await API.auth.login(username, password);

      if (response.success) {
        // Actualizar el contexto de autenticación con los datos del usuario
        setCurrentUser(response.data.usuario);

        // Redirigir al dashboard usando navigate de react-router-dom
        navigate('/dashboard');
      } else {
        setError(response.message || 'Error de autenticación');
      }
    } catch (error) {
      console.error('Error durante login:', error);
      setError(
        error.response?.data?.message ||
        error.message ||
        'Error al conectar con el servidor'
      );
    } finally {
      setLoading(false);
    }
  }

  async function handleFingerprintLogin() {
    try {
      setError('');
      setLoading(true);
      setIsCapturing(true);
      setFingerprintStatus('🖼️ Abriendo ventana oficial de verificación...');

      // Usar la nueva API de BiometricoPersona con ventana oficial de DigitalPersona
      const result = await fetch(getApiUrl(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          tipo: 'verifyBiometric'
        })
      });

      // Verificar si hay error HTTP (401, 403, 500, etc.)
      if (!result.ok) {
        if (result.status === 401) {
          setError('❌ Error 401: Huella no autorizada o no registrada en el sistema');
          setFingerprintStatus('');
          return;
        } else if (result.status === 403) {
          setError('❌ Error 403: No tiene permisos para acceder al sistema');
          setFingerprintStatus('');
          return;
        } else {
          setError(`❌ Error del servidor (${result.status}): ${result.statusText}`);
          setFingerprintStatus('');
          return;
        }
      }

      const response = await result.json();

      if (response.success) {
        if (response.data && response.data.usuario) {
          const usuario = response.data.usuario;
          setFingerprintStatus(`✅ Autenticación exitosa: ${usuario.nombre}`);

          // Guardar token si existe
          if (response.data.token) {
            localStorage.setItem('authToken', response.data.token);
          }

          // Guardar datos del usuario
          localStorage.setItem('userData', JSON.stringify(response.data.usuario));

          // Guardar datos de system y configuracion si existen
          if (response.data.system) {
            localStorage.setItem('systemConfig', JSON.stringify(response.data.system));
          }

          if (response.data.configuracion) {
            localStorage.setItem('appConfig', JSON.stringify(response.data.configuracion));
          }

          // Actualizar el contexto de autenticación con los datos del usuario
          setCurrentUser(usuario);

          // Redirigir al dashboard
          navigate('/dashboard');

        } else if (response.data && response.data.verified === true) {
          setFingerprintStatus('✅ Huella verificada correctamente');
          // Si no hay datos específicos del usuario, podríamos hacer una consulta adicional
          setError('Usuario verificado pero sin datos completos');
        } else {
          setFingerprintStatus('✅ Verificación completada');
          setError('Verificación completada pero sin datos de usuario');
        }
      } else {
        const errorMsg = response.message || 'Huella no reconocida';
        if (errorMsg.includes('no encontrada') || errorMsg.includes('no reconocida') || errorMsg.includes('no coincide')) {
          setError('❌ Huella no encontrada en la base de datos');
          setFingerprintStatus('');
        } else if (errorMsg.includes('cancelado') || errorMsg.includes('cerrado')) {
          setError('⏹️ Verificación cancelada por el usuario');
          setFingerprintStatus('');
        } else {
          setError(`❌ Error en verificación: ${errorMsg}`);
          setFingerprintStatus('');
        }
      }

    } catch (error) {
      console.error('Error en autenticación biométrica:', error);

      // Detectar tipo de error específico
      if (error.message && error.message.includes('401')) {
        setError('❌ Error 401: Huella no autorizada. Verifique que su huella esté registrada en el sistema.');
      } else if (error.message && error.message.includes('Failed to fetch')) {
        setError('❌ Error de conexión: Verifique que el servidor PHP esté corriendo (XAMPP)');
      } else {
        setError('❌ Error de conexión con el servicio biométrico');
      }
      setFingerprintStatus('');
    } finally {
      setLoading(false);
      setIsCapturing(false);

      // Limpiar el estado después de un delay si fue exitoso
      if (!error) {
        setTimeout(() => setFingerprintStatus(''), 2000);
      }
    }
  }

  return (
    <div className="login-container">
      <div className="login-form-container">
        <h1>PRESTO C2</h1>

        {/* Selector de método de autenticación */}
        <div className="auth-method-selector">
          <button
            type="button"
            className={`method-button ${loginMethod === 'password' ? 'active' : ''}`}
            onClick={() => setLoginMethod('password')}
            disabled={loading}
          >
            🔑 Contraseña
          </button>
          <button
            type="button"
            className={`method-button ${loginMethod === 'fingerprint' ? 'active' : ''}`}
            onClick={() => {
              setLoginMethod('fingerprint');
              // Iniciar automáticamente la verificación de huella
              if (loginMethod !== 'fingerprint') {
                setTimeout(() => {
                  handleFingerprintLogin();
                }, 100); // Pequeño delay para que se actualice el estado
              }
            }}
            disabled={loading}
          >
            👆 Huella Digital
          </button>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          {error && <div className="error-message">{error}</div>}
          {fingerprintStatus && (
            <div className="fingerprint-status">
              {fingerprintStatus}
              {isCapturing && <div className="loading-spinner"></div>}
            </div>
          )}

          {loginMethod === 'password' && (
            <div className="form-group">
              <label htmlFor="username">Usuario</label>
              <input
                type="text"
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
              />
            </div>
          )}

          {loginMethod === 'password' && (
            <div className="form-group">
              <label htmlFor="password">Contraseña</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required={loginMethod === 'password'}
              />
            </div>
          )}

          {loginMethod === 'fingerprint' && !loading && !isCapturing && (
            <div className="fingerprint-info">
              <p>👆 Seleccione "Huella Digital" para abrir automáticamente la ventana de verificación de DigitalPersona.</p>
            </div>
          )}

          {loginMethod === 'fingerprint' && (loading || isCapturing) && (
            <div className="fingerprint-info">
              <p>🔄 Abriendo ventana de verificación... Coloque su dedo registrado en el lector de huellas.</p>
            </div>
          )}

          {loginMethod === 'password' && (
            <button type="submit" disabled={loading || isCapturing} className="login-button">
              {loading ? 'Iniciando sesión...' : 'Iniciar sesión'}
            </button>
          )}

          {loginMethod === 'fingerprint' && !loading && !isCapturing && (
            <button
              type="button"
              onClick={handleFingerprintLogin}
              disabled={loading || isCapturing}
              className="login-button fingerprint-retry"
            >
              🔄 Reintentar verificación
            </button>
          )}
        </form>
      </div>
    </div>
  );
}

export default LoginScreen;